import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon, KeyIcon, MailIcon, ShieldCheckIcon, AlertCircleIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { validateEmail, validatePassword } from '../../utils/validation';
import { GoogleLogo } from './GoogleLogo';
import { toast } from 'sonner';
type AuthMode = 'signin' | 'otp';
export const AuthPage = () => {
  const navigate = useNavigate();
  const {
    signInWithGoogle,
    signInWithEmail,
    verifyOTP
  } = useAuth();
  const [mode, setMode] = useState<AuthMode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const handleSuccessfulAuth = () => {
    toast.dismiss();
    toast.success('Signed in successfully');
    // Force navigation to client list for all users (demo mode)
    navigate('/admin/clients', {
      replace: true
    });
  };
  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      setError('');
      toast.loading('Signing in with Google...');
      await signInWithGoogle();
      handleSuccessfulAuth();
    } catch (err) {
      toast.error('Failed to sign in with Google');
      setError('Failed to sign in with Google. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('Please enter your email');
      return;
    }
    if (!password.trim()) {
      setError('Please enter your password');
      return;
    }
    try {
      setLoading(true);
      toast.loading('Signing in...');
      await signInWithEmail(email, password);
      handleSuccessfulAuth();
    } catch (err) {
      toast.error('Invalid email or password');
      setError('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };
  const handleOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }
    try {
      setLoading(true);
      setError('');
      toast.loading('Verifying code...');
      await verifyOTP(otp);
      toast.dismiss();
      toast.success('Verification successful');
      navigate('/admin/clients', {
        replace: true
      });
    } catch (err) {
      toast.error('Invalid verification code');
      setError('Failed to verify code. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  return <div className="min-h-screen w-full bg-surface flex items-center justify-center p-4" data-id="element-217">
      <div className="w-full max-w-md space-y-8" data-id="element-218">
        {/* Logo and Title */}
        <div className="text-center" data-id="element-219">
          <div className="relative group" data-id="element-220">
            <div className="absolute inset-0 bg-accent/5 blur-xl rounded-full scale-0 group-hover:scale-100 transition-transform duration-700" data-id="element-221" />
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/1Jh6cc5VaUNbQaWHvFaseP/Sprinklenet_Logo_Black_with_Transparent_Background.png" alt="Sprinklenet" className="h-12 w-auto mx-auto invert relative transform-gpu group-hover:scale-[1.02] transition-all duration-700 ease-out" data-id="element-222" />
          </div>
          <h2 className="mt-6 text-2xl font-bold text-white font-mono" data-id="element-223">
            Welcome to Knowledge Spaces
          </h2>
          <p className="mt-2 text-sm text-gray-400" data-id="element-224">
            {mode === 'otp' ? 'Enter the verification code sent to your email' : 'Sign in to your account'}
          </p>
        </div>
        {/* Auth Forms */}
        <div className="bg-surface-light border border-surface-border p-6 rounded-none" data-id="element-225">
          {mode === 'otp' ? <form onSubmit={handleOTPSubmit} className="space-y-6" data-id="element-226">
              <div data-id="element-227">
                <label className="block text-sm font-medium text-gray-300 mb-1" data-id="element-228">
                  Verification Code
                </label>
                <div className="relative" data-id="element-229">
                  <ShieldCheckIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-230" />
                  <input type="text" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="Enter 6-digit code" className="w-full pl-10 pr-4 py-2 bg-surface border border-surface-border rounded-none 
                             text-white placeholder-gray-400 focus:outline-none focus:ring-2 
                             focus:ring-accent focus:border-transparent transition-all" maxLength={6} data-id="element-231" />
                </div>
              </div>
              <button type="submit" disabled={loading} className="w-full px-4 py-2 bg-accent hover:bg-accent-light disabled:bg-accent/50 
                         text-white rounded-none transition-colors flex items-center justify-center gap-2" data-id="element-232">
                {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-233" /> : <>
                    <ArrowRightIcon size={18} data-id="element-234" />
                    <span data-id="element-235">Verify & Continue</span>
                  </>}
              </button>
            </form> : <>
              {/* Google Sign In Button */}
              <button onClick={handleGoogleSignIn} disabled={loading} className="w-full h-10 px-3 bg-white hover:bg-gray-50 disabled:bg-gray-100 
                         text-gray-600 rounded-none transition-colors flex items-center
                         justify-center gap-2 mb-6 border border-gray-300 font-roboto
                         text-sm focus:outline-none focus:ring-2 focus:ring-[#4285F4]
                         focus:ring-offset-2" style={{
            fontFamily: "'Roboto', sans-serif"
          }} data-id="element-236">
                <GoogleLogo data-id="element-237" />
                <span className="tracking-normal" data-id="element-238">Sign in with Google</span>
              </button>
              <div className="relative" data-id="element-239">
                <div className="absolute inset-0 flex items-center" data-id="element-240">
                  <div className="w-full border-t border-surface-border" data-id="element-241" />
                </div>
                <div className="relative flex justify-center text-sm" data-id="element-242">
                  <span className="px-2 bg-surface-light text-gray-400" data-id="element-243">
                    Or continue with
                  </span>
                </div>
              </div>
              {/* Email Form */}
              <form onSubmit={handleEmailAuth} className="mt-6 space-y-6" data-id="element-244">
                <div data-id="element-245">
                  <label className="block text-sm font-medium text-gray-300 mb-1" data-id="element-246">
                    Email address
                  </label>
                  <div className="relative" data-id="element-247">
                    <MailIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-248" />
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter your email" className="w-full pl-10 pr-4 py-2 bg-surface border border-surface-border rounded-none 
                               text-white placeholder-gray-400 focus:outline-none focus:ring-2 
                               focus:ring-accent focus:border-transparent transition-all" data-id="element-249" />
                  </div>
                </div>
                <div data-id="element-250">
                  <label className="block text-sm font-medium text-gray-300 mb-1" data-id="element-251">
                    Password
                  </label>
                  <div className="relative" data-id="element-252">
                    <KeyIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-253" />
                    <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" className="w-full pl-10 pr-4 py-2 bg-surface border border-surface-border rounded-none 
                               text-white placeholder-gray-400 focus:outline-none focus:ring-2 
                               focus:ring-accent focus:border-transparent transition-all" data-id="element-254" />
                  </div>
                </div>
                <button type="submit" disabled={loading} className="w-full px-4 py-2 bg-accent hover:bg-accent-light disabled:bg-accent/50 
                           text-white rounded-none transition-colors flex items-center justify-center gap-2" data-id="element-255">
                  {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-256" /> : <>
                      <ArrowRightIcon size={18} data-id="element-257" />
                      <span data-id="element-258">Sign In</span>
                    </>}
                </button>
              </form>
            </>}
          {/* Error Message */}
          {error && <div className="mt-4 p-3 bg-status-error/10 border border-status-error/30 rounded-none flex items-start gap-2" data-id="element-259">
              <AlertCircleIcon size={18} className="text-status-error shrink-0 mt-0.5" data-id="element-260" />
              <p className="text-sm text-status-error" data-id="element-261">{error}</p>
            </div>}
        </div>
      </div>
    </div>;
};